import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";

import { testAttr } from "@/browser";
import { Button, IButtonProps } from "@/components/Button";
import { buildInputAccept, FileExtension } from "@/consts/fileExtensions";
import { IBlurable, IChangeable, Text } from "@/eworkitComponents";

interface Props extends Omit<IButtonProps, "value" | "onChange" | "onBlur" | "onClick">, IChangeable, IBlurable {
  multiple?: boolean;
  extensions: FileExtension[];
}

const defaultProps: Partial<Props> = {
  variant: "link",
};

export const FileButton: FC<Props> = memo(props => {
  const { t } = useTranslation("session");
  const { multiple, extensions, onChange, variant, ...restButtonProps } = props;

  const ref: React.RefObject<HTMLInputElement> = React.createRef();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files as FileList;
    const files = multiple ? Array.from(fileList) : fileList.length ? fileList[0] : null;

    if (onChange) {
      onChange(files);
    }
  };

  const handleButtonClick = () => {
    if (ref.current) {
      ref.current.click();
    }
  };

  return (
    <>
      <Button variant={variant} {...restButtonProps} onClick={handleButtonClick} testName="browse">
        {React.Children.count(props.children) === 0 ? (
          <Text uppercase>{t("COMPONENTS.FILE_BUTTON.BUTTON_BROWSE")}</Text>
        ) : (
          restButtonProps.children
        )}
      </Button>
      <input
        className="__file-input"
        type="file"
        ref={ref}
        onChange={handleChange}
        accept={buildInputAccept(extensions)}
        multiple={multiple}
        {...testAttr({ testName: "browse" }, { sufix: "input" })}
      />
    </>
  );
});

FileButton.defaultProps = defaultProps;
